/**
 * This Service file contains CloudSyncService, which communicates with purchaseAPI for the following purposes:
 *   1. As a plan is edited, upsert the encrypted plan and related data for analytics to purchaseAPI
 *     1a. retries queued upserts once the app is online
 *   2. When sync runs, pull the most recent plan information from purchaseAPI given an AFP for their upcoming appointments
 * */
import type { CounselorAppointment, OpportunityContactRole } from '@/apiTypes'
import Plan, { type SplitPlan } from '@/classes/Plan'
import type { CrmIdentifiers } from '@/mixins/planVersionsV2'
import { useRequestQueue } from '@/mixins/requestQueue'
import { windowRequestQueueConfig } from '@/mixins/requestQueueWindowConfig'
import { useCloudSyncDocumentService } from '@/services/CloudSync/cloudSyncDocumentService'
import type { CloudSyncInterface } from '@/services/CloudSync/cloudSyncService'
import { Feature, useFeatureService } from '@/services/featureService'
import { purchaseApiAxiosInstance } from '@/services/purchaseApiAxiosInstance'
import { useAuthStore } from '@/stores/auth'
import { useCrmStore } from '@/stores/crm'
import {
	type StoredDataEncryptionKey,
	useEncryptionStore,
} from '@/stores/encryption'
import { type StoredPlanData, usePlanStore } from '@/stores/plan'
import type { PlanBasicData, PlanData, UploadItem } from '@/types'
import { captureException } from '@sentry/vue'
import { useDebounceFn } from '@vueuse/core'
import localforageLib from 'localforage'
import { computed, ref } from 'vue'

type PlanWithIdAndLastUpdated = {
	id: string
	lastUpdated: string | undefined
}

type PlanUpsertRequest = {
	salesforceContactAfpId: string
	lastUpdated: string
	planData: StoredPlanData
	encryptionMetadata: StoredDataEncryptionKey
	crmLeadId?: string | null
	salesforceOpportunityId?: string | null
	salesforceAccountPersonAccountId?: string | null
	salesforceAccountFuneralHomeId: string
	salesforceFuneralHomeCrossSetGroupId?: string
}

export type CloudSyncFetchedPlan = {
	id: string
	crmLeadId: string
	encryptionMetadata: StoredDataEncryptionKey
	lastUpdated: string
	planData: StoredPlanData
	salesforceAccountPersonAccountId?: string
	salesforceOpportunityId?: string
}

export type Contact = Pick<
	Plan,
	| 'firstName'
	| 'middleName'
	| 'lastName'
	| 'address'
	| 'city'
	| 'state'
	| 'zip'
	| 'phone'
	| 'email'
	| 'leadId'
	| 'contactId'
	| 'opportunityId'
>

export interface CloudSyncPlanInterface
	extends CloudSyncInterface<PlanBasicData> {
	fetchFromServer: () => Promise<void>
	getByLeadId: (leadId: string) => Promise<CloudSyncFetchedPlan[]>
	getByLeadIdOrOpportunityIdOrContactId: (
		identifiers: CrmIdentifiers
	) => Promise<CloudSyncFetchedPlan[]>
	getById: (planId: string) => Promise<Plan | undefined>
	getBasicDataById: (
		planId: string
	) => Promise<(Partial<PlanBasicData> & Pick<PlanData, 'id'>) | undefined>
	loadEncryptionKeyById: (planId: string) => Promise<void>
}

export const useCloudSyncPlanService = (
	logFn: ((message: string) => void) | undefined = undefined
): CloudSyncPlanInterface => {
	const serviceName = 'plan'
	const plansToUpsert = ref<Set<string>>(new Set<string>())
	const debouncePeriod = Number(
		import.meta.env.VITE_CLOUD_SYNC_PLAN_DEBOUNCE || 15000
	)
	const localPlans = localforageLib.createInstance({
		name: 'sessions',
		storeName: 'plans',
	})

	const debugLog = (message: string): void => {
		if (logFn) {
			logFn('[Plan] ' + message)
		}
	}

	const cloudSyncPlans = localforageLib.createInstance({
		name: 'sessions',
		storeName: 'cloudSyncPlans',
	})

	const encryptionStore = useEncryptionStore()
	const crmStore = useCrmStore()
	const authStore = useAuthStore()
	const planStore = usePlanStore()
	const featureService = useFeatureService()
	const cloudSyncDocumentService = useCloudSyncDocumentService()

	const upsertQueueConfig = {
		axiosInstance: purchaseApiAxiosInstance,
		requestBackgroundSync: async (tag: string) => {
			await windowRequestQueueConfig.requestBackgroundSync('cloud-sync-upsert')
		},
		clearCompleted: true,
	}

	const upsertQueue = useRequestQueue('cloudSyncPlanUpsert', upsertQueueConfig)

	/**
	 * If we know the lastUpdated time of a plan, we can send those to CloudSync,
	 * and it will only send us newer versions if they exist.
	 */
	const getPlansWithLastUpdated = async () => {
		const planInfo: Array<PlanWithIdAndLastUpdated> = []

		try {
			// Add all local plans first
			await localPlans.iterate(function (planValue: SplitPlan, id) {
				const plan = { id, lastUpdated: planValue.basic?.lastUpdated }
				// we only filter plans that already have a timestamp, for plans that do not have a lastUpdated, we will give it one when it's edited (watcher in SessionView)
				if (plan.lastUpdated) {
					planInfo.push(plan)
				}
			})

			// Add any CloudSync plans that don't match local ID's or are newer
			await cloudSyncPlans.iterate(function (
				planValue: CloudSyncFetchedPlan,
				id
			) {
				const cloudSyncPlan = {
					id,
					lastUpdated: planValue.planData.basic.lastUpdated,
				}
				// we only filter plans that already have a timestamp, for plans that do not have a lastUpdated, we will give it one when it's edited (watcher in SessionView)
				if (!cloudSyncPlan.lastUpdated) {
					return
				}

				const foundPlanIndex = planInfo.findIndex(
					(planInfoItem) => planInfoItem.id === cloudSyncPlan.id
				)

				if (foundPlanIndex !== -1) {
					const isNewerPlan =
						Date.parse(planInfo[foundPlanIndex].lastUpdated!) <
						Date.parse(cloudSyncPlan.lastUpdated!)

					if (isNewerPlan) {
						planInfo[foundPlanIndex] = cloudSyncPlan
					}
				} else {
					planInfo.push(cloudSyncPlan)
				}
			})
		} catch (err) {
			console.error(err)
		}

		return planInfo
	}

	/**
	 * Used only when local keys indexedDB has been updated with CloudSyncPlan's DEK
	 * */
	const getById = async (planId: string): Promise<Plan | undefined> => {
		try {
			const foundPlan = await cloudSyncPlans.getItem<CloudSyncFetchedPlan>(
				planId.toLowerCase()
			)
			if (foundPlan?.planData) {
				return planStore.hydrateStoredPlan(foundPlan.planData)
			}
		} catch (e) {
			console.error('failed to get hydrated fetched plan')
		}
	}

	/**
	 * Used in newSession page when only contact card needs to be populated
	 * */
	const getBasicDataById = async (
		planId: string
	): Promise<(Partial<PlanBasicData> & Pick<PlanData, 'id'>) | undefined> => {
		try {
			const foundPlan = await cloudSyncPlans.getItem<CloudSyncFetchedPlan>(
				planId.toLowerCase()
			)
			if (foundPlan?.planData) {
				return foundPlan.planData.basic
			}
		} catch (e) {
			console.error('failed to get basic data fetched plan')
		}
	}

	/**
	 * Update local keys indexedDB with fetched DEK if a cloudSync plan version is selected
	 * */
	const loadEncryptionKeyById = async (planId: string): Promise<void> => {
		const encryptionMetadata = (
			await cloudSyncPlans.getItem<CloudSyncFetchedPlan>(planId.toLowerCase())
		)?.encryptionMetadata
		if (!!encryptionMetadata) {
			await encryptionStore.storeDek(encryptionMetadata)
		}
	}

	// Gets the old CRM lead id
	const getLeadIdsFromAppointments = () => {
		const appointments = crmStore.appointments?.data

		if (!appointments) {
			return []
		}

		const unfilteredLeadIds = appointments.map(
			(appointment: CounselorAppointment) => {
				return appointment.lead?.data.id
			}
		)

		return unfilteredLeadIds.filter((leadId) => leadId)
	}

	// New appointments will have Salesforce Opportunity and Contact ids.
	const getSalesforceIdsFromAppointments = () => {
		const appointments = crmStore.appointments?.data

		if (!appointments) {
			return { opportunityIds: [] }
		}

		let opportunityIds = appointments.map(
			(appointment: CounselorAppointment) => {
				return appointment.opportunityId
			}
		)

		opportunityIds = opportunityIds.filter((i) => i)

		const contactIdsSet = new Set()

		appointments.forEach((appointment: CounselorAppointment) => {
			appointment.contactRoles?.forEach((contactRole: OpportunityContactRole) =>
				contactIdsSet.add(contactRole.contact.data.id)
			)
		})

		return {
			opportunityIds,
			contactIds: [...contactIdsSet],
		}
	}

	// Fetch plans for an AFP, used in sync when all sync processes have completed
	const fetchFromServer = async () => {
		if (!featureService.isEnabled(Feature.CloudSync)) {
			return
		}

		const salesforceContactAfpId = authStore.getActiveSalesContactId()

		if (!salesforceContactAfpId) {
			// can't fetch without an AFP id
			return
		}

		const crmLeadIds = getLeadIdsFromAppointments()
		const salesforceIds = getSalesforceIdsFromAppointments()

		const payload = {
			salesforceContactAfpId,
			crmLeadIds,
			salesforceOpportunityIds: salesforceIds.opportunityIds,
			salesforceAccountPersonAccountIds: salesforceIds.contactIds,
			plans: await getPlansWithLastUpdated(),
		}

		try {
			const response = await purchaseApiAxiosInstance.post(
				'v1/cloud-sync/fetch-plans',
				payload
			)

			const staleRecordIds: string[] = []
			// Remove any plans that are not part of the upcoming appointments
			await cloudSyncPlans.iterate(function (
				planValue: CloudSyncFetchedPlan,
				id
			) {
				// Check if plan's leadId is included in the appointments' leadIds
				if (!crmLeadIds.includes(planValue.crmLeadId)) {
					staleRecordIds.push(id)
				}
			})

			staleRecordIds.forEach((id) =>
				cloudSyncPlans
					.removeItem(id)
					.catch(() => console.error('Could not remove old cloudSyncPlan'))
			)

			const planIdsToFetchDocFor = response.data.data.map((plan: Plan) =>
				plan.id.toLowerCase()
			)

			// fetch docs concurrently for plans
			await Promise.allSettled(
				planIdsToFetchDocFor.map((planId: string) =>
					cloudSyncDocumentService.fetchFromServer(planId)
				)
			)

			for (const plan of response.data.data) {
				await cloudSyncPlans.setItem(plan.id.toLowerCase(), normalizeUuid(plan))
			}
		} catch (error) {
			console.error(`Failed to fetch CloudSync plans at this time.`, error)
		}
	}

	const normalizeUuid = (plan: CloudSyncFetchedPlan) => {
		plan.id = plan.id.toLowerCase()

		return plan
	}

	/**
	 * Will register a planId into plansToUpsert list, plans in the list will be upserted to purchaseAPI after a debounce period
	 */
	const registerForUpsert = async (planId: string) => {
		if (!featureService.isEnabled(Feature.CloudSync)) {
			return
		}

		debugLog(`PlanId: ${planId} registered for upsert.`)

		plansToUpsert.value.add(planId)

		// call throttled upsert
		upsertPlans().catch()
	}

	const checkPlansToUpsert = () => {
		plansToUpsert.value.forEach((planId) => {
			upsertPlan(planId).catch((e) => {
				console.error(`checkPlansToUpsert failed for ${planId}`, e)
			})
		})
	}

	const flushPendingUpserts = async () => {
		try {
			await Promise.all(
				Array.from(plansToUpsert.value).map(async (planId: string) => {
					return upsertPlan(planId).catch(() =>
						console.error('Failed upserting planId: ' + planId)
					)
				})
			)

			debugLog('Successfully flushed plan sync queue')
		} catch (e) {
			debugLog('Failed to flush plan sync queue')
		}
	}
	/**
	 * Will iterate over the plansToUpsert list every debounce period, and makes a call to purchaseAPI to upsert it
	 * Updated plans will be pruned from the plansToUpsert list
	 */
	const upsertPlans = useDebounceFn(checkPlansToUpsert, debouncePeriod, {
		maxWait: debouncePeriod,
	})

	/**
	 * Retrieve an encrypted plan from local forage given the id, validate and map the data for upsert request, queue up the request
	 */
	const upsertPlan = async (planId: string) => {
		return navigator.locks.request(`cloud-sync-plan-${planId}`, async () => {
			debugLog(`Starting upsert for planId: ${planId}`)

			const plan = await localPlans.getItem<StoredPlanData>(planId)

			if (!plan || !validateForUpsert(plan.basic as unknown as PlanBasicData)) {
				debugLog(`planId: ${planId} not valid for Upsert`)
				plansToUpsert.value.delete(planId)
				return
			}

			const requestData = await mapUpsertPlanData(plan)

			const upsertRequestConfig = {
				url: `/v1/cloud-sync/plan/${planId}`,
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				data: requestData,
			}

			try {
				debugLog(`Adding ${planId} to request queue`)
				await upsertQueue.addRequest(planId, upsertRequestConfig)
			} catch (error: any) {
				console.error(
					`Error occurred while trying to upsert plan ${planId}`,
					error
				)

				const hasUnexpectedResponse =
					error.response && error.response.status !== 401
				const hasUnrecoverableError = !error.response

				if (hasUnexpectedResponse || hasUnrecoverableError) {
					captureException(error, {
						extra: {
							message:
								'Unexpected error happened during upserting Plan to CloudSync',
						},
					})
				}
			} finally {
				debugLog(`Removing planId ${planId} from debounce queue`)
				plansToUpsert.value.delete(planId)
			}
		})
	}

	const validateForUpsert = (plan: PlanBasicData): boolean => {
		const hasSalesforceContactAfpId = !!authStore.getActiveSalesContactId()
		const hasLastUpdated = !!plan.lastUpdated

		const hasFuneralHomeId = !!plan.funeralHomeId
		const hasEncryptionData = !!encryptionStore.findLocalKeyById(plan.id)

		return (
			hasSalesforceContactAfpId &&
			hasLastUpdated &&
			hasFuneralHomeId &&
			hasEncryptionData
		)
	}

	const mapUpsertPlanData = async (
		plan: StoredPlanData
	): Promise<PlanUpsertRequest> => {
		const encryptionMetadata = encryptionStore.findLocalKeyById(plan.basic.id)!
		return {
			salesforceContactAfpId: authStore.getActiveSalesContactId()!,
			lastUpdated: plan.basic.lastUpdated!,
			encryptionMetadata,
			crmLeadId: plan.basic.leadId,
			salesforceOpportunityId: plan.basic.opportunityId,
			salesforceAccountPersonAccountId: plan.basic.contactId,
			salesforceAccountFuneralHomeId: plan.basic.funeralHomeId!,
			salesforceFuneralHomeCrossSetGroupId:
				crmStore.findFuneralHome(plan.basic.funeralHomeId!)?.groupId ??
				undefined,
			planData: plan,
		}
	}

	const getPlanIdsFromUploadItem = (
		upsertRequest: UploadItem<PlanUpsertRequest>
	) => {
		return [upsertRequest.requestConfig?.data?.planData.basic.id!]
	}

	/**
	 * Retry all failed/queued upsert requests, called whenever the app is online
	 */
	const retrySync = async () => {
		try {
			await upsertQueue.retryAll()
		} catch (e) {
			console.error(`Some plans was not able to be upserted at this try.`)
		}
	}

	const getByLeadId = async (leadId: string) => {
		const plans: CloudSyncFetchedPlan[] = []
		await cloudSyncPlans.iterate(function (
			cloudSyncPlan: CloudSyncFetchedPlan
		) {
			if (cloudSyncPlan.crmLeadId.toLowerCase() === leadId.toLowerCase()) {
				plans.push(cloudSyncPlan)
			}
		})

		plans.sort((a, b) => {
			return Date.parse(b.lastUpdated) - Date.parse(a.lastUpdated)
		})

		return plans
	}

	const getByLeadIdOrOpportunityIdOrContactId = async (
		identifiers: CrmIdentifiers
	) => {
		const plans: CloudSyncFetchedPlan[] = []

		if (
			!identifiers.leadId &&
			!identifiers.opportunityId &&
			!identifiers.contactId
		) {
			return []
		}

		await cloudSyncPlans.iterate(function (
			cloudSyncPlan: CloudSyncFetchedPlan
		) {
			// We only force lowercase on leadId because it is a UUID
			// The others are Salesforce IDs.
			if (
				(!!identifiers.leadId &&
					cloudSyncPlan.crmLeadId?.toLowerCase() ===
						identifiers.leadId.toLowerCase()) ||
				(!!identifiers.opportunityId &&
					cloudSyncPlan.salesforceOpportunityId ===
						identifiers.opportunityId) ||
				(!!identifiers.contactId &&
					cloudSyncPlan.salesforceAccountPersonAccountId ===
						identifiers.contactId)
			) {
				plans.push(cloudSyncPlan)
			}
		})

		plans.sort((a, b) => {
			return Date.parse(b.lastUpdated) - Date.parse(a.lastUpdated)
		})

		return plans
	}

	const clearStorage = async () => {
		await cloudSyncPlans.clear()
	}

	const purge = async () => {
		await clearStorage()
		await upsertQueue.clearPendingRequests()
	}

	// When the app is closed or becomes invisible, all the pending plan updates is sent to purchase API
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState == 'hidden') {
			checkPlansToUpsert()
		}
	})

	const pendingDebounces = computed((): number => plansToUpsert.value.size)

	const getRelatedPlanIdsForPendingUpserts = async () => {
		const uniquePlanIds = new Set(
			await upsertQueue.getRelatedPlanIdsForPendingUpserts<PlanUpsertRequest>(
				getPlanIdsFromUploadItem
			)
		)

		return Array.from(uniquePlanIds)
	}

	return {
		debouncePeriod,
		upsertQueue,
		fetchFromServer,
		registerForUpsert,
		validateForUpsert,
		retrySync,
		getByLeadId,
		getByLeadIdOrOpportunityIdOrContactId,
		getById,
		getBasicDataById,
		getRelatedPlanIdsForPendingUpserts,
		flushPendingUpserts,
		pendingDebounces,
		clearStorage,
		purge,
		loadEncryptionKeyById,
		serviceName,
	}
}
